/**
 * TVF hero slideshow.
 *
 * Replaces the original multi-mode slider. One mode only: a looping, full-bleed
 * image hero with a segmented progress bar for navigation.
 *
 * Notes for future maintainers:
 *  - Do NOT put a transition-delay on .slideshow__track. The previous build had
 *    0.5s there, which also delayed the snap-back after a released drag. The guard
 *    meant to cancel it (.swiper-touch-moving) is not a class Swiper sets, so it
 *    never fired. That was the single worst thing about the old hero.
 *  - Looping is real looping. The old build set loop:false, and Swiper autoplay
 *    then rewound across the whole track back to slide one, which read as the
 *    slideshow playing backwards.
 */
(function () {
  "use strict";

  var DRAG_CLICK_THRESHOLD = 8; // px of travel before a tap counts as a drag

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function Slideshow(root) {
    this.root = root;
    this.swiperEl = root.querySelector(".slideshow__swiper");
    this.segments = Array.prototype.slice.call(
      root.querySelectorAll(".slideshow__segment")
    );
    this.fills = this.segments.map(function (segment) {
      return segment.querySelector(".slideshow__segment-fill");
    });
    this.reduced = prefersReducedMotion();
    this.swiper = null;
    this.autoplayOn = false;
    // Authoritative state for the progress bar. Reading the active index off
    // Swiper inside the autoplay tick is not safe: the tick restarts the clock
    // at transition start, and can fire before Swiper has moved realIndex on,
    // which wipes the outgoing segment back to empty before it refills.
    this.activeIndex = 0;
    this.fillValues = this.segments.map(function () {
      return 0;
    });
  }

  Slideshow.prototype.init = function () {
    if (!this.swiperEl || typeof window.Swiper !== "function") return;

    var slideCount = this.swiperEl.querySelectorAll(".swiper-slide").length;
    var speed = parseInt(this.root.dataset.speed, 10) || 600;
    var delay = parseInt(this.root.dataset.delay, 10) || 5000;
    var wantsAutoplay = this.root.dataset.autoplay === "true";
    var pauseOnHover = this.root.dataset.pauseOnHover === "true";

    // One slide, or a visitor who asked for less motion: nothing should move.
    var autoplayOn = wantsAutoplay && slideCount > 1 && !this.reduced;

    var options = {
      speed: this.reduced ? 0 : speed,
      effect: "slide",
      // Swiper loop needs at least (slidesPerView * 2 + 1) slides, so 3 here.
      // Every extra loopAdditionalSlides raises that requirement by 2. TVF runs
      // exactly 3 active slides, so asking for extras leaves Swiper without the
      // buffer it needs to reorder at the wrap, and the first slide loses its
      // turn. Let Swiper work out the minimum instead of forcing a number.
      loop: slideCount > 2,
      rewind: slideCount === 2,
      slidesPerView: 1,
      // Only the adapt height mode lets slides differ in height. Every other
      // mode pins one height in CSS, where autoHeight would fight it.
      autoHeight: this.root.dataset.heightMode === "adapt",
      allowTouchMove: slideCount > 1,
      grabCursor: slideCount > 1,
      watchOverflow: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true },
      // Feel: commit a swipe early, and never let a released drag crawl home.
      threshold: 5,
      followFinger: true,
      longSwipesRatio: 0.25,
      longSwipesMs: 200,
      shortSwipes: true,
      resistanceRatio: 0.65,
      autoplay: autoplayOn
        ? {
            delay: delay,
            pauseOnMouseEnter: pauseOnHover,
            disableOnInteraction: false,
            // Keep the dwell timer independent of transition completion. With
            // this left on, autoplay pauses at the start of every transition and
            // waits for a transitionend on the wrapper before resuming. The
            // instant, zero-duration reposition Swiper performs when the loop
            // wraps does not produce one, so the slide after the wrap inherits a
            // stale timer instead of a fresh delay. Off, every slide gets an
            // equal turn.
            waitForTransition: false,
          }
        : false,
    };

    this.swiper = new window.Swiper(this.swiperEl, options);
    this.autoplayOn = autoplayOn;

    this.bindProgress();
    this.bindDragAwareLinks();

    var self = this;
    this.swiper.on("slideChange", function () {
      self.syncSegments();
    });

    this.syncSegments();
  };

  /**
   * Segmented progress bar. The active segment fill is driven by Swiper's own
   * autoplay clock, so pausing on hover visibly freezes the bar instead of
   * letting it drift out of step with the slides.
   */
  Slideshow.prototype.bindProgress = function () {
    var self = this;

    this.segments.forEach(function (segment) {
      segment.addEventListener("click", function () {
        var index = parseInt(segment.dataset.slideTo, 10);
        if (isNaN(index)) return;
        if (self.swiper.params.loop) {
          self.swiper.slideToLoop(index);
        } else {
          self.swiper.slideTo(index);
        }
      });
    });

    if (!this.autoplayOn) return;

    this.swiper.on("autoplayTimeLeft", function (swiper, time, progress) {
      // progress counts 1 -> 0 across the slide dwell time.
      var index = self.activeIndex;
      var value = 1 - progress;
      // Only ever move a segment forward. Resets are the job of syncSegments,
      // which knows when the slide actually changed. Without this guard the
      // clock restarting at transition start empties the outgoing segment for
      // a frame or two before it snaps back to full.
      if (value <= self.fillValues[index]) return;
      self.setFill(index, value);
    });
  };

  Slideshow.prototype.setFill = function (index, value) {
    var fill = this.fills[index];
    if (!fill) return;
    this.fillValues[index] = value;
    fill.style.setProperty("--fill", value.toFixed(4));
  };

  Slideshow.prototype.syncSegments = function () {
    var active = this.swiper.realIndex;
    this.activeIndex = active;
    // With autoplay running the active fill is handed to the autoplay clock and
    // starts empty. With autoplay off there is no clock, so hold it full.
    var activeFill = this.autoplayOn ? 0 : 1;

    this.segments.forEach(function (segment, index) {
      var isActive = index === active;
      segment.classList.toggle("is-active", isActive);
      // A segment already played stays full until the loop comes back round.
      segment.classList.toggle("is-passed", index < active);
      segment.setAttribute("aria-selected", isActive ? "true" : "false");
    }, this);

    for (var i = 0; i < this.fills.length; i++) {
      if (i === active) {
        this.setFill(i, activeFill);
      } else {
        this.setFill(i, i < active ? 1 : 0);
      }
    }
  };

  /**
   * A slide with no buttons is itself a link. Track pointer travel and swallow
   * the click when it was a drag, so swiping the hero never navigates away.
   */
  Slideshow.prototype.bindDragAwareLinks = function () {
    var startX = 0;
    var startY = 0;
    var moved = 0;

    this.swiperEl.addEventListener(
      "pointerdown",
      function (event) {
        startX = event.clientX;
        startY = event.clientY;
        moved = 0;
      },
      { passive: true }
    );

    this.swiperEl.addEventListener(
      "pointermove",
      function (event) {
        if (event.pointerType === "mouse" && event.buttons === 0) return;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        var distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > moved) moved = distance;
      },
      { passive: true }
    );

    this.root.querySelectorAll("[data-slide-link]").forEach(function (link) {
      link.addEventListener(
        "click",
        function (event) {
          if (moved > DRAG_CLICK_THRESHOLD) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
      link.addEventListener("dragstart", function (event) {
        event.preventDefault();
      });
    });

    this.root.querySelectorAll(".slideshow__media img").forEach(function (img) {
      img.addEventListener("dragstart", function (event) {
        event.preventDefault();
      });
    });
  };

  Slideshow.prototype.destroy = function () {
    if (this.swiper) this.swiper.destroy(true, true);
    this.swiper = null;
  };

  var instances = new WeakMap();

  function initAll(scope) {
    var context = scope || document;
    context.querySelectorAll("[data-slideshow]").forEach(function (root) {
      if (instances.has(root)) return;
      var instance = new Slideshow(root);
      instances.set(root, instance);
      instance.init();
    });
  }

  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll("[data-slideshow]").forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initAll();
  });

  // Theme editor: rebuild cleanly when the section is re-rendered.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
  document.addEventListener("shopify:section:unload", function (event) {
    destroyIn(event.target);
  });
})();
